// Performance monitoring utility with enhanced tracking
import { PerformanceDashboard } from './performanceDashboard';

export class PerformanceLogger {
  private static logs: Array<{
    operation: string;
    duration: number;
    timestamp: number;
    metadata?: any;
  }> = [];

  static startTimer(operation: string) {
    const startTime = performance.now();
    return {
      operation,
      startTime,
      end: (metadata?: any) => {
        const duration = performance.now() - startTime;
        PerformanceLogger.log(operation, duration, metadata);
        return duration;
      }
    };
  }

  static log(operation: string, duration: number, metadata?: any) {
    const logEntry = {
      operation,
      duration: Math.round(duration),
      timestamp: Date.now(),
      metadata
    };

    this.logs.push(logEntry);
    
    // Keep only last 100 logs
    if (this.logs.length > 100) {
      this.logs.shift();
    }

    // Use enhanced dashboard for performance alerts
    PerformanceDashboard.logPerformanceAlert(operation, duration, metadata);
  }

  static getLogs() {
    return [...this.logs];
  }

  static getSlowOperations(threshold = 1000) {
    return this.logs.filter(log => log.duration > threshold);
  }

  static clear() {
    this.logs = [];
  }

  // Enhanced metrics via dashboard
  static getMetrics(timeframeMins = 30) {
    return PerformanceDashboard.getMetrics(timeframeMins);
  }

  static generateReport() {
    return PerformanceDashboard.generatePerformanceReport();
  }
}

// Performance wrapper for networking operations
export const withPerformanceLogging = <T extends any[], R>(
  operation: string,
  fn: (...args: T) => Promise<R>
) => {
  return async (...args: T): Promise<R> => {
    const timer = PerformanceLogger.startTimer(operation);
    try {
      const result = await fn(...args);
      timer.end({ success: true, resultCount: Array.isArray(result) ? result.length : 1 });
      return result;
    } catch (error) {
      timer.end({ success: false, error: error instanceof Error ? error.message : 'Unknown error' });
      throw error;
    }
  };
};
